import { Link } from 'react-router-dom';
import { Heart, Share2, MapPin, Star, Truck, Gavel, Handshake, Zap, Percent, Gift, Sparkles, ListPlus, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useState, useEffect } from 'react';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePromptLogin } from '@/context/LoginModalContext';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useShare } from '@/hooks/useShare';
import { SharePanel } from '@/components/common/SharePanel';
import { SafeImage } from '@/components/marketplace/SafeImage';
import { SaveToListModal } from '@/components/marketplace/SaveToListModal';
import { toast } from 'sonner';

export const AdCard = ({ ad, onFavoriteToggle }) => {
    const { role } = useAuth();
    const promptLogin = usePromptLogin();
    const { buildShareUrl } = useShareConfig();
    const [fav, setFav] = useState(ad.is_favorited || false);
    const [listModalOpen, setListModalOpen] = useState(false);
    const primary = ad.media?.[0];
    const secondary = ad.media?.[1];

    useEffect(() => { setFav(ad.is_favorited || false); }, [ad.is_favorited]);

    const toggleFav = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'favorite' });
            if (!ok) return;
        }
        try {
            const { data } = await api.post('/favorites', { ad_id: ad.id });
            setFav(data.favorited);
            onFavoriteToggle?.(ad.id, data.favorited);
        } catch {
            toast.error('No se pudo actualizar favorito');
        }
    };

    const handleSaveToList = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'generic' });
            if (!ok) return;
        }
        setListModalOpen(true);
    };

    const cardShareUrl = buildShareUrl(`/a/${ad.id}`);
    const { share, panelOpen: sharePanelOpen, closePanel: closeSharePanel } = useShare(cardShareUrl, ad.title);

    // Stock display
    const hasStock = typeof ad.stock === 'number';
    const lowStock = hasStock && ad.stock > 0 && ad.stock <= 5;

    return (
        <>
            <Link to={`/ad/${ad.id}`} data-testid="ad-card" className="group relative block">
                <div className="relative overflow-hidden rounded-2xl border bg-card shadow-[var(--ok-shadow-sm)] hover:shadow-[var(--ok-shadow-card)] transition-shadow duration-200">
                    <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--ok-surface-2))]">
                        {primary?.kind === 'youtube' ? (
                            <iframe
                                src={`https://www.youtube.com/embed/${primary.url}?autoplay=1&mute=1&loop=1&playlist=${primary.url}&controls=0&modestbranding=1&playsinline=1`}
                                title={ad.title}
                                className="absolute inset-0 h-full w-full pointer-events-none"
                                allow="autoplay; encrypted-media"
                                frameBorder="0"
                            />
                        ) : primary?.kind === 'video' ? (
                            <video src={mediaUrl(primary.url)} className="absolute inset-0 h-full w-full object-cover" muted autoPlay loop playsInline />
                        ) : primary?.url ? (
                            <SafeImage src={primary.url} alt={ad.title} className="absolute inset-0 h-full w-full object-cover transition-opacity duration-300" />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">Sin foto</div>
                        )}
                        {secondary && secondary.kind === 'image' && (
                            <SafeImage
                                src={secondary.url}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                            />
                        )}
                        <div className="absolute left-2 top-2 flex flex-wrap gap-1 max-w-[75%]">
                            <Badge variant="secondary" className="bg-white/90 text-foreground border-0 capitalize text-[10px]">{ad.condition?.replace('_', ' ')}</Badge>
                            {ad.shipping_available && <Badge variant="secondary" className="bg-white/90 text-foreground border-0 text-[10px]"><Truck className="h-2.5 w-2.5 mr-0.5" />Envío</Badge>}
                            {ad.sale_mode === 'auction' && <Badge className="bg-amber-500 text-white border-0 text-[10px]"><Gavel className="h-2.5 w-2.5 mr-0.5" />Subasta</Badge>}
                            {ad.sale_mode === 'offers' && <Badge className="bg-blue-500 text-white border-0 text-[10px]"><Handshake className="h-2.5 w-2.5 mr-0.5" />Ofertas</Badge>}
                            {ad.flash_sale && <Badge className="bg-rose-500 text-white border-0 text-[10px] animate-pulse"><Zap className="h-2.5 w-2.5 mr-0.5" />Flash</Badge>}
                        </div>
                        {ad.discount_percent > 0 && (
                            <div className="absolute right-2 bottom-2">
                                <Badge className="bg-emerald-500 text-white border-0 text-[11px] font-bold px-2 py-1"><Percent className="h-3 w-3 mr-0.5" />-{Math.round(ad.discount_percent)}%</Badge>
                            </div>
                        )}
                        {ad.cashback_percent > 0 && (
                            <div className="absolute left-2 bottom-2">
                                <Badge className="bg-amber-500 text-white border-0 text-[10px] font-bold"><Gift className="h-2.5 w-2.5 mr-0.5" />Cashback {ad.cashback_percent}%</Badge>
                            </div>
                        )}
                        <div className="absolute right-2 top-2 flex flex-col gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                            <button
                                data-testid="ad-card-favorite-button"
                                onClick={toggleFav}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm hover:bg-white transition-colors"
                                aria-label="Guardar en favoritos"
                            >
                                <Heart className={`h-4 w-4 ${fav ? 'fill-rose-500 text-rose-500' : 'text-foreground'}`} />
                            </button>
                            <button
                                onClick={handleSaveToList}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm hover:bg-white transition-colors"
                                aria-label="Guardar en lista"
                            >
                                <ListPlus className="h-4 w-4 text-foreground" />
                            </button>
                            <div className="relative">
                                <button
                                    data-testid="ad-card-share-button"
                                    onClick={share}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-sm hover:bg-white transition-colors"
                                    aria-label="Compartir"
                                >
                                    <Share2 className="h-4 w-4" />
                                </button>
                                <SharePanel
                                    url={cardShareUrl}
                                    title={ad.title}
                                    isOpen={sharePanelOpen}
                                    onClose={closeSharePanel}
                                    className="top-10 right-0"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="p-3 sm:p-4">
                        <div className="flex items-baseline gap-2">
                            <div data-testid="ad-card-price" className="money text-xl sm:text-2xl font-semibold">{formatMoney(ad.price, ad.currency)}</div>
                            {ad.compare_at_price && ad.compare_at_price > ad.price && (
                                <div className="text-xs text-muted-foreground line-through">{formatMoney(ad.compare_at_price, ad.currency)}</div>
                            )}
                        </div>
                        <div data-testid="ad-card-title" className="mt-1 line-clamp-2 text-sm font-medium text-foreground">{ad.title}</div>
                        {ad.points_reward > 0 && (
                            <div className="mt-1 text-[10px] text-amber-700 font-medium inline-flex items-center gap-0.5">
                                <Sparkles className="h-3 w-3" />Gana {ad.points_reward} pts
                            </div>
                        )}
                        {/* Stock indicator */}
                        {hasStock && (
                            <div className={`mt-1 text-[10px] font-medium inline-flex items-center gap-0.5 ${ad.stock === 0 ? 'text-red-500' : lowStock ? 'text-orange-600' : 'text-green-700'}`}>
                                <Package className="h-3 w-3" />
                                {ad.stock === 0 ? 'Sin stock' : lowStock ? `¡Solo ${ad.stock} disponibles!` : `Stock: ${ad.stock}`}
                            </div>
                        )}
                        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{ad.location}</span>
                            <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />@{ad.seller_nickname}</span>
                        </div>
                    </div>
                </div>
            </Link>

            <SaveToListModal open={listModalOpen} onClose={() => setListModalOpen(false)} adId={ad.id} />
        </>
    );
};
